import {
  ActionIcon,
  Button,
  CloseButton,
  Group,
  Loader,
  Modal,
  ScrollArea,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useDebouncedCallback } from "@mantine/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Box, Check, Eye, Globe, Search, Upload, User, type LucideIcon } from "lucide-react";
import { useState } from "react";

import {
  getListActorsQueryKey,
  listActors,
} from "../../api/generated/endpoints/generation/generation";
import type { AssetOrder, ListActorsParams } from "../../api/generated/model";
import { ErrorAlert } from "../ErrorAlert";
import { LIBRARY_ORDERS, useLibraryPages } from "../media/library-pages";
import { EmptyState } from "../../ui/EmptyState";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { SortMenu } from "../../ui/SortMenu";
import { ThumbImage } from "../../ui/ThumbImage";
import { ActorFacets, NO_ACTOR_FILTER, facetParams, type ActorFilter } from "./ActorFacets";
import classes from "./AddActorsDialog.module.css";
import { MediaPreview } from "../media/MediaPreview";
import { hasVideo, pickedVideoItem } from "./actor-preview";
import { pickedOfActor, pickedOfAsset, type PickedActor } from "./picked";

/** Live's page of actors in the dialog, and its search's wait while typing (`search-input`). */
const PAGE = 24;
const SEARCH_WAIT_MS = 500;
/** The route's limit on a search. */
const SEARCH_MAX = 100;

type Source = "global" | "team" | "creatives" | "uploads";

/**
 * Live's four sources, in its order, words and icons (`add-actors-modal/.../constants.ts`): the
 * global and the team's actors, and the library's images, made (Creatives) or uploaded.
 */
const SOURCES: { value: Source; label: string; icon: LucideIcon }[] = [
  { value: "global", label: "Global", icon: Globe },
  { value: "team", label: "Team Actors", icon: User },
  { value: "creatives", label: "Creatives", icon: Box },
  { value: "uploads", label: "Manual Uploads", icon: Upload },
];

export interface AddActorsDialogProps {
  teamId: string;
  brandId: string;
  /** The actors the form holds, chosen when the dialog opens. */
  chosen: PickedActor[];
  /** How many may be chosen: live's four less the actors still being made. */
  max: number;
  onClose: () => void;
  /** The actors chosen, in the order they were picked. */
  onConfirm: (actors: PickedActor[]) => void;
}

/**
 * Live's "Add Actors" (`add-actors-modal`, capture L09): a near full-screen dialog with live's
 * search (applied as typed) and sort, its sources at the left (Global with live's facets applied
 * as they change, Team Actors, the library's Creatives and Manual Uploads), the actors as a grid
 * of portraits, and Confirm, grey until one is chosen, and Cancel. A click chooses or unchooses;
 * past the cap, a pick takes the place of the last, as live's. The search and the sort hold
 * across the sources, as live's; the facets are the global actors' only.
 */
export function AddActorsDialog(props: AddActorsDialogProps) {
  const { teamId, brandId, chosen, max, onClose, onConfirm } = props;
  const [source, setSource] = useState<Source>("global");
  const [typed, setTyped] = useState("");
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<AssetOrder>("newest");
  const [filter, setFilter] = useState<ActorFilter>(NO_ACTOR_FILTER);
  const [selected, setSelected] = useState<PickedActor[]>(chosen);
  // The actor the lightbox shows, by its key; null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const applySearch = useDebouncedCallback((value: string) => setSearch(value.trim()), SEARCH_WAIT_MS);
  const pick = (actor: PickedActor) =>
    setSelected((current) => {
      if (current.some((each) => each.key === actor.key)) {
        return current.filter((each) => each.key !== actor.key);
      }
      return current.length < max ? [...current, actor] : [...current.slice(0, max - 1), actor];
    });
  const library = source === "creatives" || source === "uploads";
  const params: ListActorsParams = {
    kind: source === "team" ? "team" : "global",
    ...(order === "newest" ? {} : { order }),
    ...(search === "" ? {} : { search }),
    ...(source === "global" ? facetParams(filter) : {}),
  };
  const actors = useInfiniteQuery({
    // The page's own key for these pages, so both read one cache.
    queryKey: [...getListActorsQueryKey(teamId, brandId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listActors(
        teamId,
        brandId,
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: !library,
  });
  // Live's Creatives and Manual Uploads: the brand's images, made or uploaded, as the library
  // lists them.
  const images = useLibraryPages(
    teamId,
    brandId,
    {
      kind: "image",
      trash: false,
      source: [source === "creatives" ? "generation" : "upload"],
      search,
      order,
    },
    library,
    { size: PAGE },
  );
  const list = library ? images : actors;
  const items: PickedActor[] = library
    ? (images.data?.pages.flatMap((page) => page.items) ?? []).map(pickedOfAsset)
    : (actors.data?.pages.flatMap((page) => page.items) ?? []).map(pickedOfActor);
  return (
    <Modal.Root
      opened
      onClose={onClose}
      // While its lightbox is open, Escape is the lightbox's alone: one Escape closes the topmost
      // layer (Mantine's modal listens on the window, under the lightbox's own).
      closeOnEscape={previewing === null}
      centered
      classNames={{ content: classes.content }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header className={classes.header}>
          <Modal.Title className={classes.title}>Add Actors</Modal.Title>
          <Modal.CloseButton aria-label="Close" />
        </Modal.Header>
        <Modal.Body className={classes.body}>
          <div className={classes.toolbar}>
            <TextInput
              className={classes.search}
              aria-label="Search actors"
              placeholder="Search actors"
              leftSection={<Search size={16} aria-hidden />}
              maxLength={SEARCH_MAX}
              value={typed}
              onChange={(event) => {
                setTyped(event.currentTarget.value);
                applySearch(event.currentTarget.value);
              }}
              rightSection={
                typed === "" ? null : (
                  <CloseButton
                    size="sm"
                    aria-label="Clear input"
                    onClick={() => {
                      setTyped("");
                      setSearch("");
                    }}
                  />
                )
              }
            />
            <SortMenu options={LIBRARY_ORDERS} value={order} onChange={setOrder} />
          </div>
          <div className={classes.columns}>
            <div className={classes.sidebar}>
              <fieldset className={classes.sources} aria-label="Sources">
                {SOURCES.map((each) => {
                  const Icon = each.icon;
                  return (
                    <UnstyledButton
                      key={each.value}
                      className={classes.source}
                      data-active={each.value === source || undefined}
                      aria-pressed={each.value === source}
                      onClick={() => setSource(each.value)}
                    >
                      <Icon size={18} strokeWidth={1.5} aria-hidden />
                      <span>{each.label}</span>
                    </UnstyledButton>
                  );
                })}
              </fieldset>
              {source === "global" ? (
                <ScrollArea type="hover" offsetScrollbars className={classes.facets}>
                  <ActorFacets value={filter} onChange={setFilter} />
                </ScrollArea>
              ) : null}
            </div>
            <div className={classes.scroll}>
              {list.isPending ? <Loader className={classes.loader} aria-label="Loading the actors" /> : null}
              {list.isError ? <ErrorAlert error={list.error} /> : null}
              {list.isSuccess && items.length === 0 ? (
                // Live's words for an empty list (`constants/common.ts`).
                <EmptyState
                  title="No results found"
                  description="Check for typos or filters, or try a different search query."
                />
              ) : null}
              <fieldset className={classes.grid} aria-label="Actors">
                {items.map((actor) => (
                  <Tile
                    key={actor.key}
                    actor={actor}
                    checked={selected.some((each) => each.key === actor.key)}
                    onSelect={() => pick(actor)}
                    onPreview={actor.videoUrl === null ? undefined : () => setPreviewing(actor.key)}
                  />
                ))}
              </fieldset>
              {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
              <LoadAhead more={morePages(list)} loaded={items.length} label="Loading more actors" />
            </div>
          </div>
          <Group justify="center" gap={12} className={classes.footer}>
            <Button
              disabled={selected.length === 0}
              onClick={() => {
                onConfirm(selected);
                onClose();
              }}
            >
              Confirm
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </Group>
        </Modal.Body>
      </Modal.Content>
      {/* Live's eye opens the actor's video; ours, the shared lightbox over the dialog (issue
          828, bug 25), stepping through the listed actors with a video. */}
      <MediaPreview
        items={items.filter(hasVideo).map(pickedVideoItem)}
        current={previewing}
        onShow={setPreviewing}
        more={morePages(list)}
      />
    </Modal.Root>
  );
}

interface TileProps {
  actor: PickedActor;
  checked: boolean;
  onSelect: () => void;
  /** Live's eye on a global actor, which opens its video; none without one. */
  onPreview: (() => void) | undefined;
}

/** An actor to choose: its portrait and name, live's ring and check once chosen. */
function Tile({ actor, checked, onSelect, onPreview }: TileProps) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={classes.tile} data-checked={checked || undefined}>
      <label className={classes.pick}>
        <input
          type="checkbox"
          className={classes.input}
          aria-label={actor.name}
          checked={checked}
          onChange={onSelect}
        />
        {failed || actor.imageUrl === null ? (
          <span className={classes.placeholder} aria-hidden>
            {actor.name}
          </span>
        ) : (
          // The 512 px portrait, else the portrait itself (issue 828, item 12).
          <ThumbImage thumb={actor.thumbUrl} full={actor.imageUrl} alt="" className={classes.picture} onLost={() => setFailed(true)} />
        )}
        <span className={classes.name} aria-hidden>
          {actor.name}
        </span>
        {checked ? (
          <span className={classes.check} aria-hidden>
            <Check size={16} strokeWidth={3} />
          </span>
        ) : null}
      </label>
      {onPreview === undefined ? null : (
        <ActionIcon
          className={classes.eye}
          variant="filled"
          aria-label={`Preview ${actor.name}`}
          onClick={onPreview}
        >
          <Eye size={16} />
        </ActionIcon>
      )}
    </div>
  );
}
